import { FeaturedImageFragment } from '../fragments/FeaturedImage';
import { ContributorsFragment } from '../fragments/Contributors';
import { minifyGraphQL } from '../utils';
import executeGraphQlRequest from './executeGraphQlRequest';
import { resolvePostImage } from '../lib/resolvePostImage';
import type { TransformedItemPost } from '../types';

type OriginalNode = {
  node: {
    id: string;
    databaseId: number;
    date: string;
    publishedDate?: string;
    modified?: string;
    customDateType?: string;
    excerpt: string;
    title: string;
    uri: string;
    slug: string;
    categories?: { nodes?: Array<{ name?: string | null } | null> | null };
    featuredImage?: {
      node: {
        altText: string;
        publicId: string;
        sourceUrl: string;
        mediaDetails?: { meta?: { credit?: string } };
      };
    };
    contributors?: { types?: unknown[] };
  };
};

const categoriesField = `
  categories(first: 1) {
    nodes {
      name
    }
  }
`;

const nodeFields = (postTypeGql: string) => `
  id
  ... on ${postTypeGql} {
    databaseId date: customDate publishedDate: date modified customDateType excerpt title uri slug
    ${categoriesField}
    ...FeaturedImageFragment ...ContributorsFragment
  }
  ... on Post { databaseId date: customDate publishedDate: date modified customDateType excerpt title uri slug ${categoriesField} featuredImage { node { publicId sourceUrl altText mediaDetails { meta { credit } } } } contributors { types { key value { title } } } }
  ... on Page { databaseId date: customDate publishedDate: date modified customDateType excerpt title uri slug ${categoriesField} featuredImage { node { publicId sourceUrl altText mediaDetails { meta { credit } } } } contributors { types { key value { title } } } }
`;

/**
 * Per-category window used when intersecting multiple category result sets.
 * BC WPGraphQL does not expose taxQuery on contentNodes, so AND is implemented
 * by intersecting aliased category(id) queries. Fetching only `limit` per
 * category drops true overlap posts outside each category's top-N; over-fetch
 * then slice restores usable AND results (e.g. ranking ∩ business).
 */
export const MULTI_CATEGORY_FETCH_WINDOW = 100;

/**
 * When a single category is provided, uses the category(id, idType: SLUG) root field.
 * When multiple categories are provided, issues one aliased category(id, idType: SLUG)
 * field per slug; the caller intersects results to implement AND logic.
 * When no categories are provided, queries contentNodes directly for latest posts.
 */
export function embeddedPostsByCatQuery(
  postTypeGql: string,
  categories: string[] = [],
): string {
  if (categories.length > 1) {
    const catFields = categories
      .map(
        (slug, i) =>
          `cat${i}: category(id: ${JSON.stringify(slug)}, idType: SLUG) {
    contentNodes(first: $limit, where: {orderby: {field: $orderBy, order: DESC}}) {
      edges {
        node {
          ${nodeFields(postTypeGql)}
        }
      }
    }
  }`,
      )
      .join('\n  ');
    return `
${FeaturedImageFragment(postTypeGql)}
${ContributorsFragment(postTypeGql)}
query EmbeddedPostsByCat($limit: Int!, $orderBy: PostObjectsConnectionOrderbyEnum!) {
  ${catFields}
}
`;
  }

  if (categories.length === 1) {
    return `
${FeaturedImageFragment(postTypeGql)}
${ContributorsFragment(postTypeGql)}
query EmbeddedPostsByCat($category: ID!, $limit: Int!, $orderBy: PostObjectsConnectionOrderbyEnum!) {
  category(id: $category, idType: SLUG) {
    contentNodes(first: $limit, where: {orderby: {field: $orderBy, order: DESC}}) {
      edges {
        node {
          ${nodeFields(postTypeGql)}
        }
      }
    }
  }
}
`;
  }

  return `
${FeaturedImageFragment(postTypeGql)}
${ContributorsFragment(postTypeGql)}
query EmbeddedPostsByCat($limit: Int!, $orderBy: PostObjectsConnectionOrderbyEnum!) {
  contentNodes(first: $limit, where: {
    orderby: { field: $orderBy, order: DESC }
  }) {
    edges {
      node {
        ${nodeFields(postTypeGql)}
      }
    }
  }
}
`;
}

const OrderBy: Record<string, string> = {
  updated: 'MODIFIED',
  published: 'DATE',
  custom: 'CUSTOM_DATE',
};

type Filters = { limit?: string; orderBy?: 'updated' | 'published' | 'custom' };

const edgeTimestamp = (n: OriginalNode['node']): number => {
  const raw =
    n.customDateType === 'show_published_date'
      ? n.publishedDate || n.date
      : n.modified || n.date;
  return raw && !Number.isNaN(Date.parse(raw)) ? Date.parse(raw) : 0;
};

/**
 * Fetches posts filtered by category slug(s).
 * - Single category: uses the category(id, idType: SLUG) root field.
 * - Multiple categories: aliased category fields per slug, intersect by
 *   databaseId (AND), sort, then slice to the requested limit. Over-fetches
 *   each category up to MULTI_CATEGORY_FETCH_WINDOW because BC GraphQL has no
 *   taxQuery on contentNodes.
 * - Empty array or no categories: returns latest posts via contentNodes.
 */
export default async function fetchEmbeddedPostsByCat(
  category: string | string[],
  filters: Filters,
  postTypeGql: string,
  cmsUrl: string,
  fallbackPostImage?: string,
): Promise<TransformedItemPost[] | null> {
  const categories = Array.isArray(category)
    ? category
    : category
      ? [category]
      : [];

  const query = minifyGraphQL(embeddedPostsByCatQuery(postTypeGql, categories));
  const { limit = '8', orderBy = 'updated' } = filters;
  const floored = Math.floor(Number(limit));
  const limitValue =
    Number.isNaN(floored) || floored < 1 ? 8 : Math.min(floored, 100);
  const orderByValue = OrderBy[orderBy] || 'MODIFIED';
  const isMultiCategory = categories.length > 1;
  const fetchLimit = isMultiCategory
    ? Math.max(limitValue, MULTI_CATEGORY_FETCH_WINDOW)
    : limitValue;

  const variables: Record<string, unknown> = {
    limit: fetchLimit,
    orderBy: orderByValue,
  };
  if (categories.length === 1) {
    variables['category'] = categories[0];
  }

  try {
    let edges: OriginalNode[];

    if (isMultiCategory) {
      const data = await executeGraphQlRequest<
        Record<string, { contentNodes?: { edges: OriginalNode[] } } | undefined>
      >({ cmsUrl, query, variables });
      const sets = categories.map(
        (_, i) => data?.[`cat${i}`]?.contentNodes?.edges ?? [],
      );
      const [first = [], ...rest] = sets;
      const intersection = rest.length
        ? first.filter((e) =>
            rest.every((s) =>
              s.some((r) => r.node.databaseId === e.node.databaseId),
            ),
          )
        : first;
      if (!intersection.length) return null;
      edges = [...intersection]
        .sort((a, b) => edgeTimestamp(b.node) - edgeTimestamp(a.node))
        .slice(0, limitValue);
    } else {
      const data = await executeGraphQlRequest<{
        category?: { contentNodes?: { edges: OriginalNode[] } };
        contentNodes?: { edges: OriginalNode[] };
      }>({ cmsUrl, query, variables });
      const rawEdges =
        categories.length === 1
          ? data?.category?.contentNodes?.edges
          : data?.contentNodes?.edges;
      if (!rawEdges?.length) return null;
      edges = rawEdges;
    }

    const transformedData = edges.map((edge: OriginalNode) => {
      const publicId = edge.node.featuredImage?.node?.publicId;
      const srcUrl = edge.node.featuredImage?.node?.sourceUrl;
      const displayIso =
        edge.node.customDateType === 'show_published_date'
          ? edge.node.publishedDate || edge.node.date
          : edge.node.modified || edge.node.date;
      const date =
        displayIso && !Number.isNaN(Date.parse(displayIso))
          ? new Date(displayIso).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })
          : '';
      const types = edge.node.contributors?.types as
        | { key: string; value?: { title?: string }[] }[]
        | undefined;
      const contributorType =
        types?.find((t) => t.key === 'author' || t.key === 'writer') ||
        types?.[0];
      const contributorName = contributorType?.value?.[0]?.title;
      const categoryBrow =
        edge.node.categories?.nodes?.[0]?.name?.trim() || undefined;
      const item: TransformedItemPost = {
        id: String(edge.node.databaseId),
        title: edge.node.title || '',
        link: edge.node.uri || '',
        image:
          resolvePostImage(publicId ?? '', {}, srcUrl, fallbackPostImage) ??
          undefined,
        imagealt:
          edge.node.featuredImage?.node?.altText || edge.node.title || '',
        content: edge.node.excerpt || '',
        date: date || '',
        brow: categoryBrow,
        contributors: contributorName ? [{ name: contributorName }] : undefined,
      };
      return item;
    });
    return transformedData;
  } catch {
    return null;
  }
}
